import React, { useState, useEffect, useRef } from 'react';
import { Container, Button, Row, Col } from 'react-bootstrap';
import { FaArrowLeft, FaArrowRight, FaClock, FaUsers, FaBookOpen } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import '../../styles/courseCarousel.css';

const CourseCarousel = ({ courses, title = "Popular Courses" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const [visibleCourses, setVisibleCourses] = useState([]);
  const navigate = useNavigate();
  const autoPlayRef = useRef(null);

  // Calculate visible cards based on screen width
  const calculateVisibleCount = () => {
    const width = window.innerWidth;
    if (width < 768) return 1;
    if (width < 992) return 2;
    return 3;
  };

  // Update visible courses
  const updateVisibleCourses = () => {
    const count = visibleCount;
    const total = courses.length;
    const coursesToShow = [];
    
    for (let i = 0; i < count; i++) {
      const index = (currentIndex + i) % total;
      coursesToShow.push(courses[index]);
    }
    
    setVisibleCourses(coursesToShow);
  };

  // Next slide
  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % courses.length);
  };

  // Previous slide
  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + courses.length) % courses.length);
  };

  // Go to specific slide
  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Auto-play setup
  const startAutoPlay = () => {
    autoPlayRef.current = setInterval(() => {
      nextSlide();
    }, 3000);
  };

  const stopAutoPlay = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const newCount = calculateVisibleCount();
      setVisibleCount(newCount);
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update visible courses when index or visibleCount changes
  useEffect(() => {
    if (courses.length > 0) {
      updateVisibleCourses();
    }
  }, [currentIndex, visibleCount, courses]);

  // Auto-play control
  useEffect(() => {
    if (courses.length > visibleCount) {
      startAutoPlay();
    }
    return () => stopAutoPlay();
  }, [courses.length, visibleCount]);

  // Handle hover to pause auto-play
  const handleMouseEnter = () => {
    stopAutoPlay();
  };

  const handleMouseLeave = () => {
    if (courses.length > visibleCount) {
      startAutoPlay();
    }
  };

  // Format student count
  const formatStudentCount = (count) => {
    if (!count) return '0 students';
    if (count > 1000) return `${Math.floor(count / 1000)}k+ students`;
    return `${count} students`;
  };

  // Format duration
  const formatDuration = (duration) => {
    if (!duration) return 'Flexible';
    return duration;
  };

  if (!courses.length) {
    return (
      <div className="course-carousel-empty">
        <Container className="text-center py-5">
          <FaBookOpen size={50} className="text-muted mb-3" />
          <h5>No courses available</h5>
          <p className="text-muted">Check back later for new courses.</p>
        </Container>
      </div>
    );
  }

  const totalSlides = courses.length;
  const totalDots = totalSlides - visibleCount + 1;

  return (
    <div 
      className="course-carousel-section"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Container>
        {title && <h2 className="course-carousel-title">{title}</h2>}
        
        <div className="course-carousel-container">
          {/* Previous Button */}
          <button className="carousel-arrow prev-arrow" onClick={prevSlide}>
            <FaArrowLeft />
          </button>

          {/* Cards Container */}
          <div className="carousel-cards-wrapper">
            <Row className="g-4 justify-content-center">
              {visibleCourses.map((course, idx) => {
                const totalStudents = (course.base_student_count || 0) + (course.enrolled_students || 0);
                return (
                  <Col key={course.id} lg={12 / visibleCount} md={6} sm={12} className="carousel-col">
                    <div className="carousel-card">
                      {/* Course Image */}
                      <div className="carousel-card-image-wrapper">
                        {course.image_url ? (
                          <img
                            src={course.image_url}
                            alt={course.name}
                            className="carousel-card-image"
                          />
                        ) : (
                          <div className="carousel-card-image-placeholder">
                            <FaBookOpen size={40} className="text-primary" />
                          </div>
                        )}
                      </div>

                      {/* Course Content */}
                      <div className="carousel-card-content">
                        <h3 className="carousel-card-title">{course.name}</h3>
                        <div className="carousel-card-meta">
                          <span className="meta-item">
                            <FaClock className="meta-icon" />
                            {formatDuration(course.duration)}
                          </span>
                          <span className="meta-item">
                            <FaUsers className="meta-icon" />
                            {formatStudentCount(totalStudents)}
                          </span>
                        </div>
                        <p className="carousel-card-description">
                          {course.description?.substring(0, 80)}...
                        </p>
                        <Button
                          variant="outline-primary"
                          className="carousel-card-btn"
                          onClick={() => navigate(`/courses/${course.id}`)}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>

          {/* Next Button */}
          <button className="carousel-arrow next-arrow" onClick={nextSlide}>
            <FaArrowRight />
          </button>
        </div>

        {/* Dots Indicator */}
        {totalDots > 1 && (
          <div className="carousel-dots">
            {Array.from({ length: totalDots }).map((_, idx) => (
              <button
                key={idx}
                className={`carousel-dot ${idx === currentIndex ? 'active' : ''}`}
                onClick={() => goToSlide(idx)}
              />
            ))}
          </div>
        )}

        {/* Explore All Button */}
        <div className="carousel-explore-btn-wrapper">
          <Button 
            variant="primary" 
            size="lg"
            className="carousel-explore-btn"
            onClick={() => navigate('/courses')}
          >
            Explore All Courses →
          </Button>
        </div>
      </Container>
    </div>
  );
};

export default CourseCarousel;